import { colors, spacing } from "@procore/core-react";
import type {
   CategoryNodeData,
   FilteredProjectCategoryNode,
   ProjectCategory,
   ProjectCategoryNode,
} from "./types";

export const DropZoneIntent = {
   BEFORE: "before",
   AFTER: "after",
   ON: "on",
} as const;
export type DropZoneIntent = (typeof DropZoneIntent)[keyof typeof DropZoneIntent];
const { BEFORE, AFTER, ON } = DropZoneIntent;
export const MAX_DEPTH = 5;
export const BORDER = `1px solid ${colors.gray85}`;
export const CATEGORIES_TRANSLATIONS =
   "views.company.workforce_planning.project_details_categories";

/**
 * Computes the total width of the grip handle area at a given tree depth.
 * Matches the layout in CategoryRow: paddingLeft + icon + paddingRight + borderRight.
 * @param depth - the tree depth level
 * @returns the width in pixels
 */
export function gripWidth(depth: number): number {
   return Math.max(depth * spacing.sm, spacing.xs) + spacing.xl + spacing.xs + 1;
}

/**
 * Recursive function to flatten a given node's children
 *  after their parent in viewing order
 * @param node the current node
 * @param out the flat viewing order array
 */
function collectNodes(node: ProjectCategoryNode, out: ProjectCategoryNode[]): void {
   out.push(node);
   for (const child of node.children) {
      collectNodes(child, out);
   }
}

/**
 * Converts the tree of category nodes into a flat viewing order array
 * @param roots the roots of the category tree
 * @param showSubcategories whether we include subcategories
 * @returns a flattened array of categories in viewing order
 */
export function flattenCategoryTree(
   roots: ProjectCategoryNode[],
   showSubcategories: boolean,
): ProjectCategoryNode[] {
   if (showSubcategories) {
      const rows: ProjectCategoryNode[] = [];
      roots.forEach((root) => collectNodes(root, rows));
      return rows;
   }

   return [...roots];
}

/**
 * Finds all matching nodes and their ancesters to return in a flat array.
 * @param roots the roots of the category tree
 * @param query the string we'll try to match category names against
 * @returns all nodes that matched and their ancestors
 */
export const filterCategories = (
   roots: ProjectCategoryNode[],
   query: string,
): FilteredProjectCategoryNode[] => {
   const normalizedQuery = query.toLowerCase();
   const results: FilteredProjectCategoryNode[] = [];

   const hasMatch = (node: ProjectCategoryNode): boolean => {
      if (node.name.toLowerCase().includes(normalizedQuery)) return true;
      if (node.depth >= MAX_DEPTH) return false;
      return node.children.some((child) => hasMatch(child));
   };

   const traverse = (node: ProjectCategoryNode) => {
      const selfMatches = node.name.toLowerCase().includes(normalizedQuery);
      const include =
         selfMatches || (node.depth < MAX_DEPTH && node.children.some((child) => hasMatch(child)));
      if (!include) return;
      results.push({ ...node, children: [], matched: selfMatches });
      if (node.depth < MAX_DEPTH) {
         for (const child of node.children) traverse(child);
      }
   };

   for (const root of roots) traverse(root);

   return results;
};

/**
 * Sets depth on every node and returns count of descendants of `node`.
 * @param node the current node to set depth
 * @param depth the current depth of the tree
 * @returns the number of descendants beneath `node`
 */
function initializeSubtree(node: ProjectCategoryNode, depth: number): number {
   node.depth = depth;
   if (depth >= MAX_DEPTH) return 0;
   let descendants = 0;
   for (const child of node.children) {
      descendants += 1 + initializeSubtree(child, depth + 1);
   }
   return descendants;
}

/**
 * Converts an array of project categories sorted by sequence
 *  into an array-based tree with order maintained per child array
 * @param categories project categories, ordered by sequence
 * @returns an array-based tree
 */
export const flatToTree = (categories: ProjectCategory[]): ProjectCategoryNode[] => {
   const nodeMap = new Map<string, ProjectCategoryNode>();

   for (const category of categories) {
      nodeMap.set(category.id, { ...category, children: [], depth: 0 });
   }

   const roots: ProjectCategoryNode[] = [];

   for (const node of nodeMap.values()) {
      if (node.parent_id === null) {
         roots.push(node);
      } else {
         const parent = nodeMap.get(node.parent_id);
         if (parent) {
            parent.children.push(node);
         }
      }
   }

   for (const root of roots) {
      root.descendantCount = initializeSubtree(root, 0);
   }

   return roots;
};

/**
 * top 10%    → BEFORE
 * middle 80% → ON
 * bottom 10% → AFTER
 * @param pointerY y-coordinate of pointer
 * @param rect bounding rectangle of the row the pointer is in
 * @param allowOn determines whether we allow dropping on the zone, defaults to true
 * @returns the drop zone intent
 */
export const detectDropZone = (pointerY: number, rect: DOMRect, allowOn = true): DropZoneIntent => {
   const ratio = (pointerY - rect.top) / rect.height;
   if (!allowOn) return ratio < 0.5 ? BEFORE : AFTER;
   if (ratio < 0.1) return BEFORE;
   if (ratio > 0.9) return AFTER;
   return ON;
};

/**
 * Maps a pointer X position to a depth level within a valid range.
 * Scans from deepest to shallowest, returning the deepest depth whose
 * grip area the pointer is to the right of.
 * @param pointerX - viewport X coordinate of the pointer
 * @param rowLeft - left edge of the row element (viewport coordinate)
 * @param minDepth - shallowest valid depth
 * @param maxDepth - deepest valid depth
 * @returns the resolved depth
 */
export const resolveDepthFromPointerX = (
   pointerX: number,
   rowLeft: number,
   minDepth: number,
   maxDepth: number,
): number => {
   for (let d = maxDepth; d > minDepth; d--) {
      if (pointerX >= rowLeft + gripWidth(d)) return d;
   }
   return minDepth;
};

/**
 * Walks up from a node through parent_id links to find its ancestor at a given depth.
 * The caller must ensure targetDepth < the starting node's depth.
 * @param rows - flat visible rows (each has id, parent_id, depth)
 * @param nodeId - the starting node's id
 * @param targetDepth - the depth of the desired ancestor
 * @returns the ancestor node, or null if not found
 */
export const findAncestorAtDepth = (
   rows: ProjectCategoryNode[],
   nodeId: string,
   targetDepth: number,
): ProjectCategoryNode | null => {
   const nodeMap = new Map(rows.map((r) => [r.id, r]));
   let current = nodeMap.get(nodeId) ?? null;
   while (current && current.depth > targetDepth) {
      current = (current.parent_id && nodeMap.get(current.parent_id)) || null;
   }
   return current?.depth === targetDepth ? current : null;
};

export type CategoryMoveUpdate = {
   id: string;
   parent_id: string | null;
   sequence: number;
};
export type CategoryDropData = CategoryNodeData & {
   intent: DropZoneIntent;
};

function maxDepthInSubtree(
   rootId: string,
   depth: number,
   map: Map<string, ProjectCategory>,
): number {
   let maxDepth = depth;
   for (const c of map.values()) {
      if (c.parent_id === rootId) {
         maxDepth = Math.max(maxDepth, maxDepthInSubtree(c.id, depth + 1, map));
      }
   }
   return maxDepth;
}

function isStrictDescendantOf(
   ancestorId: string,
   nodeId: string,
   map: Map<string, ProjectCategory>,
): boolean {
   let cur = map.get(nodeId)?.parent_id;
   while (cur) {
      if (cur === ancestorId) return true;
      cur = map.get(cur)?.parent_id;
   }
   return false;
}

const DropError = {
   MOVE_SELF_DROP: "move_self_drop",
   MOVE_NOOP: "move_noop",
   MOVE_INTO_DESCENDANT: "move_into_descendant",
   MOVE_MAX_DEPTH: "move_max_depth",
} as const;

/**
 * Derives the new parent_id from the drop target based on the drop zone intent.
 * before/after → same parent as the target; into → the target itself.
 */
function validateDrop(
   categories: ProjectCategory[],
   source: CategoryNodeData,
   target: CategoryDropData,
): void {
   if (target.id === source.id) {
      throw new Error(DropError.MOVE_SELF_DROP);
   }

   const newParentId = target.intent === ON ? target.id : target.parent_id;

   if (source.parent_id === newParentId) {
      if (target.intent === BEFORE && source.sequence + 1 === target.sequence) {
         throw new Error(DropError.MOVE_NOOP);
      }
      if (target.intent === AFTER && source.sequence - 1 === target.sequence) {
         throw new Error(DropError.MOVE_NOOP);
      }
      if (target.intent === ON) {
         const childCount = categories.filter((c) => c.parent_id === target.id).length - 1;
         if (source.sequence === childCount) {
            throw new Error(DropError.MOVE_NOOP);
         }
      }
   }

   if (newParentId !== null) {
      const map = new Map(categories.map((c) => [c.id, c]));
      if (isStrictDescendantOf(source.id, newParentId, map)) {
         throw new Error(DropError.MOVE_INTO_DESCENDANT);
      }

      const parentDepth = target.intent === ON ? target.depth : target.depth - 1;
      const newSubtreeMaxDepth =
         parentDepth + maxDepthInSubtree(source.id, source.depth, map) - source.depth + 1;

      if (newSubtreeMaxDepth > MAX_DEPTH) {
         throw new Error(DropError.MOVE_MAX_DEPTH);
      }
   }
}

/**
 * Checks whether a category is the dragged category itself or one of its descendants.
 * Used during drag-move to suppress drop indicators.
 * @param categories - Flat list of all categories.
 * @param draggedId - The id of the category being dragged.
 * @param categoryId - The id of the category to test.
 * @returns True if categoryId is a strict descendant of draggedId.
 */
/**
 * Resolves an AFTER drop intent into a more specific target when the drop
 * position is ambiguous (e.g. between a parent and its first child, or at
 * the end of a subtree where multiple ancestor depths are valid).
 * @param data - the node data of the current drop target
 * @param rows - flat visible rows in viewing order
 * @param pointerX - viewport X coordinate of the pointer, or null
 * @param rect - bounding rect of the target row element
 * @returns a resolved drop target, or null to keep the default
 */
export const resolveAfterDropTarget = (
   data: CategoryNodeData,
   rows: ProjectCategoryNode[],
   pointerX: number | null,
   rect: DOMRect,
): CategoryDropData | null => {
   const targetIndex = rows.findIndex((r) => r.id === data.id);
   if (targetIndex === -1) return null;

   const nextRow = rows[targetIndex + 1];

   // Next row is a child — rewrite as BEFORE the first child
   if (nextRow?.depth > data.depth) {
      return {
         id: nextRow.id,
         name: nextRow.name,
         sequence: nextRow.sequence,
         parent_id: nextRow.parent_id,
         depth: nextRow.depth,
         intent: BEFORE,
      };
   }

   // No depth ambiguity — keep default
   const minDepth = nextRow?.depth ?? 0;
   if (minDepth >= data.depth) return null;
   if (pointerX == null) return null;

   // Multiple valid depths — use pointer X to pick one
   const resolvedDepth = resolveDepthFromPointerX(pointerX, rect.left, minDepth, data.depth);
   if (resolvedDepth >= data.depth) return null;

   const ancestor = findAncestorAtDepth(rows, data.id, resolvedDepth);
   if (!ancestor) return null;

   return {
      id: ancestor.id,
      name: ancestor.name,
      sequence: ancestor.sequence,
      parent_id: ancestor.parent_id,
      depth: ancestor.depth,
      intent: AFTER,
   };
};

export const isCategoryInDraggedTree = (
   categories: ProjectCategory[],
   draggedId: string,
   categoryId: string,
): boolean => {
   if (draggedId === categoryId) return true;
   const map = new Map(categories.map((c) => [c.id, c]));
   return isStrictDescendantOf(draggedId, categoryId, map);
};

/**
 * Resolves a drop intent into a validated {@link CategoryMoveUpdate}.
 * Throws with a translation key suffix (e.g. "move_max_depth") on invalid drops.
 * @param categories - Flat list of all categories.
 * @param source - The dragged category's node data (without children).
 * @param target - The drop target data including zone intent.
 * @returns The validated move update payload for the backend.
 */
export const getCategoryMoveUpdate = (
   categories: ProjectCategory[],
   source: CategoryNodeData,
   target: CategoryDropData,
): CategoryMoveUpdate => {
   validateDrop(categories, source, target);

   if (target.intent === BEFORE) {
      return {
         id: source.id,
         parent_id: target.parent_id,
         sequence: target.sequence,
      };
   }

   if (target.intent === ON) {
      return {
         id: source.id,
         parent_id: target.id,
         sequence: categories.filter((c) => c.parent_id === target.id && c.id !== source.id).length,
      };
   }

   return {
      id: source.id,
      parent_id: target.parent_id,
      sequence: target.sequence + 1,
   };
};
